import React, { useState, useEffect } from 'react';
import { getDeviceIssues } from '../utils/deviceIssuesUtils';
import './DeviceIssuesSummary.css';

/**
 * Compact chip-based device attention banner (shared by view + edit).
 */
function DeviceIssuesSummary({
  device,
  user = null,
  onNavigateToComplaintsWithFilter = null,
  hidden = false,
}) {
  const [issuesExpanded, setIssuesExpanded] = useState(false);

  useEffect(() => {
    setIssuesExpanded(false);
  }, [device?.id]);

  if (hidden || !device) return null;

  const deviceIssues = getDeviceIssues(device, user);
  if (!deviceIssues.severity) return null;

  return (
    <div className={`device-issues-summary severity-${deviceIssues.severity}`}>
      <div className="device-issues-summary-main">
        <div className="device-issues-summary-copy">
          <div className="device-issues-title-row">
            <span className="device-issues-title">Attention needed</span>
            <span className="device-issues-count-summary">
              {[
                deviceIssues.critical.length > 0
                  ? `${deviceIssues.critical.length} critical`
                  : null,
                deviceIssues.attention.length > 0
                  ? `${deviceIssues.attention.length} attention`
                  : null,
                deviceIssues.upcoming.length > 0
                  ? `${deviceIssues.upcoming.length} upcoming`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
          <div className="device-issues-chips" aria-label="Device issues">
            {deviceIssues.chips.map((issue) => {
              const severityClass = deviceIssues.critical.some((c) => c.key === issue.key)
                ? 'chip-critical'
                : deviceIssues.attention.some((a) => a.key === issue.key)
                  ? 'chip-attention'
                  : 'chip-upcoming';
              const canOpenComplaints =
                issue.isComplaint &&
                issue.showComplaintNav &&
                onNavigateToComplaintsWithFilter &&
                device.device_identifier;

              return (
                <span
                  key={issue.key}
                  className={`device-issue-chip ${severityClass}${
                    canOpenComplaints ? ' has-nav' : ''
                  }`}
                >
                  {issue.chip}
                  {canOpenComplaints && (
                    <button
                      type="button"
                      className="device-issue-chip-nav"
                      title="View complaints for this device"
                      aria-label={
                        issue.key === 'complaints-critical'
                          ? `View critical complaints for ${device.device_identifier}`
                          : issue.key === 'complaints-other'
                            ? `View non-critical complaints for ${device.device_identifier}`
                            : `View complaints for ${device.device_identifier}`
                      }
                      onClick={() => {
                        const payload = {
                          search: device.device_identifier,
                          statuses: [
                            'open',
                            'acknowledged',
                            'in_progress',
                            'escalated_to_vendor',
                          ],
                          daysOpenFrom: '',
                          daysOpenTo: '',
                        };
                        if (issue.complaintNavSeverities?.length) {
                          payload.severities = issue.complaintNavSeverities;
                        }
                        onNavigateToComplaintsWithFilter(payload);
                      }}
                    >
                      <span className="device-issue-chip-nav-icon" aria-hidden="true" />
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        </div>
        <button
          type="button"
          className={`device-issues-toggle${issuesExpanded ? ' is-expanded' : ''}`}
          aria-expanded={issuesExpanded}
          aria-label={issuesExpanded ? 'Hide details' : 'Show details'}
          onClick={() => setIssuesExpanded((prev) => !prev)}
        >
          <span className="device-issues-chevron" aria-hidden="true" />
        </button>
      </div>
      {issuesExpanded && (
        <ul className="device-issues-details">
          {deviceIssues.details.map((issue) => (
            <li key={`detail-${issue.key}`}>{issue.detail}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default DeviceIssuesSummary;
